import React, { useState } from 'react';
import SEO from '../components/SEO';

const destinationsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Popular US Flight Destinations',
  description: 'Top destinations to fly to from major US cities with best fares.',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Los Angeles, CA', url: 'https://flyairlinebooking.com/flights?destination=LAX' },
    { '@type': 'ListItem', position: 2, name: 'New York, NY',    url: 'https://flyairlinebooking.com/flights?destination=JFK' },
    { '@type': 'ListItem', position: 3, name: 'Miami, FL',       url: 'https://flyairlinebooking.com/flights?destination=MIA' },
    { '@type': 'ListItem', position: 4, name: 'Las Vegas, NV',   url: 'https://flyairlinebooking.com/flights?destination=LAS' },
    { '@type': 'ListItem', position: 5, name: 'Honolulu, HI',    url: 'https://flyairlinebooking.com/flights?destination=HNL' },
  ],
};

const DESTINATIONS = [
  { code: 'LAX', city: 'Los Angeles', state: 'California', emoji: '🌴', desc: 'Hollywood, beaches & perfect weather year-round.', popular: true },
  { code: 'JFK', city: 'New York', state: 'New York', emoji: '🗽', desc: 'The city that never sleeps — culture, food & finance hub.', popular: true },
  { code: 'MIA', city: 'Miami', state: 'Florida', emoji: '🏖️', desc: 'Vibrant nightlife, art deco, and stunning beaches.', popular: true },
  { code: 'LAS', city: 'Las Vegas', state: 'Nevada', emoji: '🎰', desc: 'Entertainment capital of the world.', popular: true },
  { code: 'HNL', city: 'Honolulu', state: 'Hawaii', emoji: '🌺', desc: 'Tropical paradise with world-class surf and Aloha spirit.', popular: true },
  { code: 'ORD', city: 'Chicago', state: 'Illinois', emoji: '🌬️', desc: 'Architecture, deep-dish pizza and Lake Michigan views.', popular: false },
  { code: 'SFO', city: 'San Francisco', state: 'California', emoji: '🌉', desc: 'Golden Gate, tech innovation and Victorian charm.', popular: false },
  { code: 'SEA', city: 'Seattle', state: 'Washington', emoji: '☕', desc: 'Coffee culture, tech scene and the Space Needle.', popular: false },
  { code: 'DEN', city: 'Denver', state: 'Colorado', emoji: '🏔️', desc: 'Gateway to the Rockies — ski, hike and explore.', popular: false },
  { code: 'ATL', city: 'Atlanta', state: 'Georgia', emoji: '🍑', desc: 'Southern hospitality, history and a booming food scene.', popular: false },
  { code: 'MCO', city: 'Orlando', state: 'Florida', emoji: '🎢', desc: 'Theme park capital — Disney, Universal and more.', popular: false },
  { code: 'AUS', city: 'Austin', state: 'Texas', emoji: '🤠', desc: 'Live music, BBQ, tech startups and Texan spirit.', popular: false },
];

export default function DestinationsPage() {
  const [filter, setFilter] = useState('all');

  const today = new Date();
  today.setDate(today.getDate() + 14);
  const date = today.toISOString().split('T')[0];

  const filtered = filter === 'popular'
    ? DESTINATIONS.filter(d => d.popular)
    : DESTINATIONS;

  return (
    <div>
      <SEO
        title="Flight Destinations"
        canonical="/destinations"
        description="Explore popular US flight destinations — Los Angeles, New York, Miami, Las Vegas, Honolulu and more. Find cheap fares and book in one click."
        keywords="flight destinations, popular US cities, cheap flights to Miami, flights to LA, flights to New York, travel destinations USA"
        jsonLd={destinationsJsonLd}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-900 to-green-900 text-white py-20 px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Explore Destinations</h1>
        <p className="text-blue-100 text-lg max-w-xl mx-auto">
          Discover top US destinations and find the best fares to get there.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Filter tabs */}
        <div className="flex gap-3 mb-8">
          {[{ key: 'all', label: 'All Destinations' }, { key: 'popular', label: '🔥 Most Popular' }].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-5 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${
                filter === f.key
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(dest => (
            <div key={dest.code} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-200 group">
              {/* Color bar */}
              <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-3xl">{dest.emoji}</span>
                      <div>
                        <h3 className="font-extrabold text-gray-900 text-lg leading-tight">{dest.city}</h3>
                        <p className="text-gray-400 text-xs">{dest.state} · {dest.code}</p>
                      </div>
                    </div>
                  </div>
                  {dest.popular && (
                    <span className="bg-emerald-100 text-orange-600 text-xs font-bold px-2 py-1 rounded-full">Popular</span>
                  )}
                </div>
                <p className="text-gray-500 text-sm mb-5 leading-relaxed">{dest.desc}</p>
                <a
                  href={`/flights?origin=JFK&destination=${dest.code}&date=${date}&passengers=1&class=economy`}
                  className="block text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
                >
                  Find Flights →
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
